'use client'

import { useState, useMemo } from 'react'
import { cn } from '@paitox/ui/lib/utils'
import { Search, Plus, User, Phone, Mail, CheckCircle2 } from 'lucide-react'
import { db, Staff as DbStaff } from '../lib/db'

export function StaffPage() {
  const [staffList, setStaffList] = useState<DbStaff[]>(() => db.getStaff())
  const [search, setSearch] = useState('')
  const [showNew, setShowNew] = useState(false)

  // Creation fields
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [specialties, setSpecialties] = useState<string[]>([])
  const [color, setColor] = useState('#06b6d4')

  const availableSpecialties = ['facial', 'masajes', 'uñas', 'depilación']

  const filtered = useMemo(() => {
    if (!search) return staffList
    return staffList.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || s.specialties.join(' ').toLowerCase().includes(search.toLowerCase()))
  }, [staffList, search])

  const handleCreate = () => {
    if (!name) return

    const newStaff: DbStaff = {
      id: `st-${Date.now()}`,
      name,
      email: email || `${name.toLowerCase().replace(' ', '')}@paitox.cl`,
      phone: phone || '+56900000000',
      specialties: specialties.length > 0 ? specialties : ['facial'],
      color,
      isActive: true
    }

    const updated = [...staffList, newStaff]
    setStaffList(updated)
    db.saveStaff(updated)
    setShowNew(false)

    setName('')
    setEmail('')
    setPhone('')
    setSpecialties([])
    setColor('#06b6d4')
  }

  const toggleActive = (id: string) => {
    const updated = staffList.map(s => s.id === id ? { ...s, isActive: !s.isActive } : s)
    setStaffList(updated)
    db.saveStaff(updated)
  }

  const handleSpecialtyCheckbox = (spec: string) => {
    if (specialties.includes(spec)) {
      setSpecialties(specialties.filter(s => s !== spec))
    } else {
      setSpecialties([...specialties, spec])
    }
  }

  const initialsOf = (fullName: string) => {
    return fullName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[hsl(220,18%,96%)] p-4 gap-4">
      <div className="flex items-center justify-between gap-3 shrink-0">
        <div>
          <h1 className="text-lg font-bold text-slate-800">Especialistas (Staff)</h1>
          <p className="text-xs text-slate-500">{staffList.length} especialistas registrados en el equipo</p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-3 h-9 text-sm font-semibold rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 transition shadow-sm">
          <Plus className="h-4 w-4" /> Nuevo especialista
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap shrink-0">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por especialista o especialidad..."
            className="w-full h-9 pl-9 pr-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 transition"
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(staff => (
            <div key={staff.id} className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5 hover:shadow-md transition-all flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start gap-2">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-inner" style={{ backgroundColor: staff.color }}>
                      {initialsOf(staff.name)}
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-slate-800">{staff.name}</h3>
                      <span className="text-[10px] text-slate-400 font-medium">ID: {staff.id}</span>
                    </div>
                  </div>
                  <button onClick={() => toggleActive(staff.id)}
                    className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full border transition", staff.isActive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-400 border-slate-200")}>
                    {staff.isActive ? 'Activo' : 'Inactivo'}
                  </button>
                </div>

                <div className="mt-4 space-y-2.5">
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span>{staff.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{staff.email}</span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">Especialidades:</span>
                    <div className="flex flex-wrap gap-1">
                      {staff.specialties.map(spec => (
                        <span key={spec} className="text-[10px] font-semibold bg-cyan-50/60 text-cyan-700 border border-cyan-100 px-2 py-0.5 rounded-md capitalize">
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                <span className="flex items-center gap-1.5 font-medium">
                  <span className="h-2 w-2 rounded-full block" style={{ backgroundColor: staff.color }} />
                  Asignado en Calendario
                </span>
                <span className="font-medium text-cyan-600">Sync Google Calendar</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowNew(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-800 mb-4">Añadir Nuevo Especialista</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Nombre Completo *</label>
                <input type="text" placeholder="Ej: Marcela Paz" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Correo Electrónico</label>
                <input type="email" placeholder="correo@paitox.cl" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Teléfono de Contacto</label>
                <input type="text" placeholder="+569..." className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={phone} onChange={e => setPhone(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Especialidades (Seleccione todas las que correspondan)</label>
                <div className="grid grid-cols-2 gap-2">
                  {availableSpecialties.map(spec => (
                    <label key={spec} className="flex items-center gap-2 text-xs text-slate-700 capitalize cursor-pointer">
                      <input type="checkbox" checked={specialties.includes(spec)} onChange={() => handleSpecialtyCheckbox(spec)} className="rounded text-cyan-600 focus:ring-cyan-400" />
                      {spec}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Color Identificador en Calendario</label>
                <div className="flex items-center gap-2">
                  <input type="color" className="h-9 w-14 border border-slate-200 rounded-lg cursor-pointer p-0.5" value={color} onChange={e => setColor(e.target.value)} />
                  <span className="text-xs text-slate-500 font-mono">{color.toUpperCase()}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="flex-1 h-9 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition">Cancelar</button>
              <button onClick={handleCreate} disabled={!name} className="flex-1 h-9 bg-cyan-600 text-white rounded-xl text-sm font-semibold hover:bg-cyan-700 transition disabled:opacity-50">Guardar Especialista</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
